/**
 * Maps thrown query/loader errors to user-facing, in-world copy.
 *
 * The most common failure — the content API being unreachable — must never
 * surface as a raw "TypeError: fetch failed". `fetch()` rejects with a
 * `TypeError` when the network/proxy can't be reached, and the proxy reports an
 * upstream-down condition as a gateway status (502/503/504). Both mean the same
 * thing to a reader: the Veil hasn't parted.
 */
import { ApiError } from './client'
import { isChunkLoadError } from '@/lib/staleChunk'

/** Shown when the content API can't be reached at all (network down, proxy down, gateway error). */
export const VEIL_UNREACHABLE =
  "The Veil isn't parted to allow this lore through — the archive could not be reached. Try again once it stirs."

/**
 * True when the error means "the API server could not be reached", as opposed to
 * a normal HTTP error (404 not found, 403 forbidden, …) that carries real meaning.
 */
export function isApiUnreachable(error: unknown): boolean {
  // A failed `import()` of a route chunk (stale build after a deploy) is also a
  // TypeError, but it is not the archive being down — keep its own message.
  if (isChunkLoadError(error)) return false
  // fetch() rejects with a TypeError when the network/proxy is unreachable
  // ("fetch failed" in Node/SSR, "Failed to fetch" / "NetworkError" / "Load failed" in browsers).
  if (error instanceof TypeError) return true
  // The proxy surfaces an upstream-down condition as a gateway status.
  if (error instanceof ApiError && [0, 502, 503, 504].includes(error.status)) return true
  if (
    error instanceof Error &&
    /fetch failed|failed to fetch|networkerror|network request failed|load failed|econnrefused/i.test(error.message)
  ) {
    return true
  }
  return false
}

/**
 * Themed message for any thrown error. Connection failures become the
 * "Veil isn't parted" copy; every other error keeps its own message (so a real
 * 404/403/503 body still reads true), falling back to a themed default.
 */
export function apiErrorMessage(error: unknown, fallback = 'The veil obscures this content.'): string {
  if (isApiUnreachable(error)) return VEIL_UNREACHABLE
  if (error instanceof Error && error.message) return error.message
  return fallback
}
